import { PayerRow } from '../../admin-configuration/models/payers';
import { PatientCurrentInsurance } from '../../patient/components/models/patient-view-model';
import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { Vob, PayorPlan, Estimate, FacilityLevelOfCare } from '@finpay/estimation-types';
import { isSelfPayPayor, findSelfPayPayor } from '../../shared/utils/insurance-utils';

export interface EstimateValidationResult {
  isValid: boolean;
  warnings: string[];
  errors: string[];
}

export interface EstimateValidationContext {
  estimate: Estimate;
  selectedPatientCurrentInsurance?: PatientCurrentInsurance;
  selectedEncounter?: PatientEncounter;
  vob?: Vob | null;
  rateAsPayorPlan?: PayorPlan | null;
  payersMap: Map<number, PayerRow>;
  facilityLevelsOfCareList?: FacilityLevelOfCare[] | null;
}

export class EstimateValidationService {
  
  static validateEstimate(
    context: EstimateValidationContext, 
    options: { onSave?: boolean } = {}
  ): EstimateValidationResult {
    const warnings: string[] = [];
    const errors: string[] = [];

    if (!context.estimate) {
      errors.push('No estimate provided for validation');
      return { isValid: false, warnings, errors };
    }

    const isOpenEstimate = this.isOpenEstimate(context.estimate, context.selectedEncounter);
    
    if (!isOpenEstimate && !options.onSave) {
      // skip validation for estimates attached to converted IOCs unless saving
      return { isValid: true, warnings, errors };
    }

    // validate payment type vs patient insurance status
    this.validatePaymentTypeVsInsurance(context, warnings, errors, options.onSave);

    // validate VOB
    this.validateVOB(context, warnings, errors, options.onSave);

    // validate Level of Care matching
    // will revisit, for now handled in loadAndSet function
    // this.validateLevelOfCareMatching(context, warnings, errors, options.onSave);

    // validate admission date
    this.validateAdmissionDate(context, warnings, errors, options.onSave);

    return {
      isValid: errors.length === 0,
      warnings,
      errors
    };
  }

  // Checks if an estimate is "open" (not attached to converted IOC)

  private static isOpenEstimate(estimate: any, selectedEncounter?: PatientEncounter): boolean {
    const estimateEncounterId = estimate?.patientEncounterId || estimate?.finPay?.patientEncounterId;
    if (!estimateEncounterId) {
      return true;
    }

    if (!selectedEncounter) {
      return true;
    }

    if (estimateEncounterId !== selectedEncounter.patientEncounterId) {
      return true;
    }

    const isConverted = selectedEncounter.isConverted || selectedEncounter.workflow?.workflowId === 3;
    return !isConverted;
  }

  // validates estimate payment method against patient's current insurance status

  private static validatePaymentTypeVsInsurance(
    context: EstimateValidationContext, 
    warnings: string[], 
    errors: string[], 
    onSave?: boolean
  ): void {
    const { estimate, selectedPatientCurrentInsurance, payersMap } = context;

    if (!selectedPatientCurrentInsurance || !payersMap) {
      return; 
    }

    const isPatientSelfPay = isSelfPayPayor(selectedPatientCurrentInsurance.payorId, payersMap);
    const isPatientUnInsured = isPatientSelfPay;

    // Self Pay validation: Patient is insured but estimate is marked as Self Pay
    if (estimate?.isSelfPay && !isPatientSelfPay) {
      errors.push("Estimate is configured to not use insurance but patient is insured. Review and update estimate and/or patient.");
      return;
    }

    // Private Pay validation: Patient chose private pay but is uninsured
    if (estimate?.isPrivatePay && isPatientUnInsured) {
      const message = 'Estimate is set to Private Pay but patient is set to Uninsured.';
      if (onSave) {
        errors.push(message);
      } else {
        warnings.push(message);
      }
    }

    // Insurance estimate but patient has no coverage
    if (!estimate?.isSelfPay && !estimate?.isPrivatePay && isPatientSelfPay) {
      const message = 'Estimate is configured to use insurance but patient is marked as Self-Pay (no coverage). Review and update estimate and/or patient.';
      if (onSave) {
        errors.push(message);
      } else {
        warnings.push(message);
      }
    }
  }

  // validates VOB status and requirements

  private static validateVOB(
    context: EstimateValidationContext, 
    warnings: string[], 
    errors: string[], 
    onSave?: boolean
  ): void {
    const { estimate, vob } = context;

    if (estimate?.isSelfPay || estimate?.isPrivatePay) {
      return;
    }

    if (!vob || !vob.vobId) {

      const message = 'Valid VOB is required for insurance estimates';
      if (onSave) {
        errors.push(message);
      } else {
        warnings.push(message);
      }
      return;
    }

    // check VOB validity
    if (vob.isValid === false) {
      const message = 'VOB is marked as invalid';
      if (onSave) {
        errors.push(message);
      } else {
        warnings.push(message);
      }
    }

    // check VOB policy end date
    if (vob.isValid && !vob.policyEndDate) {
      const message = 'Valid VOB must have a policy end date';
      if (onSave) {
        errors.push(message);
      } else {
        warnings.push(message);
      }
    }
  }

  // validates Level of Care matching between estimate and rate-as payor plan

  private static validateLevelOfCareMatching(
    context: EstimateValidationContext, 
    warnings: string[], 
    errors: string[], 
    onSave?: boolean
  ): void {
    const { estimate, rateAsPayorPlan, facilityLevelsOfCareList } = context;

    // skip validation if no payor plan or facility LOCs
    const estimateLOCs = estimate?.facilityLevelOfCare;
    if (!rateAsPayorPlan?.plan?.facilityPlanId || !facilityLevelsOfCareList || !estimateLOCs) {
      return;
    }

    const availableLOCIds = facilityLevelsOfCareList
      .filter((loc: any) => loc.facilityPayorPlanId === rateAsPayorPlan.plan.facilityPlanId)
      .map((loc: any) => loc.facilityLevelOfCareId);

    const estimateLOCIds = estimateLOCs.map((loc: any) => loc.facilityLevelOfCareId);
    const invalidLOCs = estimateLOCIds.filter((locId: any) => !availableLOCIds.includes(locId));

    if (invalidLOCs.length > 0) {
      const message = `Estimate Level of Care need updates for current Rate-as Payer. Edit the Level of Care list.`;
      if (onSave) {
        errors.push(message);
      } else {
        warnings.push(message);
      }
    }
  }

  // validates admission date is not in the past

  private static validateAdmissionDate(
    context: EstimateValidationContext, 
    warnings: string[], 
    errors: string[], 
    onSave?: boolean
  ): void {
    return;
  }

  // quick validation for estimate loading - only shows warnings

  static validateEstimateOnLoad(context: EstimateValidationContext): EstimateValidationResult {
    return this.validateEstimate(context, { onSave: false });
  }

  // strict validation for save operations - shows errors that block saving

  static validateEstimateOnSave(context: EstimateValidationContext): EstimateValidationResult {
    return this.validateEstimate(context, { onSave: true });
  }

  // validates estimate for IOC creation - strictest validation

  static validateEstimateForIOC(context: EstimateValidationContext, isUpdate: boolean = false): EstimateValidationResult {
    const result = this.validateEstimate(context, { onSave: true });
    
    // For IOC updates, the estimate should already be linked to the encounter
    if (isUpdate) {
      const estimateEncounterId = context.estimate?.patientEncounterId;
      if (!estimateEncounterId) {
        result.errors.push('Estimate must be linked to an IOC');
      }
    }
    return result;
  }
}
